/* eslint-disable no-eval */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, InputGroup, Button, Dropdown, ButtonGroup } from 'react-bootstrap';
import Autocomplete from '../Autocomplete';
import CEP from '../CEP';
import FileUpload from '../FileUpload';
import Range from '../Range';
import DatePicker from 'react-datepicker';
import RadioGroup from '../RadioGroup';
import GroupCheckbox from '../GroupCheckbox';
import { NumericFormat } from 'react-number-format';
import { formatCPF, validateCPF } from 'common/utils/cpf';
import { validateEmail } from 'common/utils/email';
import { formatCPFCNPJ, validateCPFCNPJ } from 'common/utils/cpf-cnpj';
import { formatText } from 'common/utils/textCase';
import { formatCurrency, formatDecimal } from 'common/utils/currency';
import { addDays, parseDate } from 'common/utils/date';
import { formatPhoneNumber, validatePhoneNumber } from 'common/utils/phoneNumber';
import pt from 'date-fns/locale/pt-BR';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import Decimal from 'decimal.js'; // resolve problemas de precisão com números decimais (pontos flutuantes)
import './style.css'

// Função para aplicar máscara customizada
const applyMask = (value, mask) => {
  if (!mask || !value) return value;
  
  // Suporta array de máscaras: ["AAA-9999", "AAA-1A11"]
  const masks = Array.isArray(mask) ? mask : [mask];
  
  // Função auxiliar para aplicar uma única máscara
  const applySingleMask = (val, msk) => {
    let maskedValue = '';
    let valueIndex = 0;
    
    for (let i = 0; i < msk.length && valueIndex < val.length; i++) {
      if (msk[i] === '9') {
        // 9 = dígito obrigatório
        if (/\d/.test(val[valueIndex])) {
          maskedValue += val[valueIndex];
          valueIndex++;
        } else {
          break;
        }
      } else if (msk[i] === 'A') {
        // A = letra obrigatória
        if (/[a-zA-Z]/.test(val[valueIndex])) {
          maskedValue += val[valueIndex];
          valueIndex++;
        } else {
          break;
        }
      } else if (msk[i] === 'X') {
        // X = alfanumérico obrigatório
        if (/[a-zA-Z0-9]/.test(val[valueIndex])) {
          maskedValue += val[valueIndex];
          valueIndex++;
        } else {
          break;
        }
      } else {
        // Caractere fixo da máscara
        maskedValue += msk[i];
      }
    }
    
    return maskedValue;
  };
  
  // Se houver apenas uma máscara, aplica diretamente
  if (masks.length === 1) {
    return applySingleMask(value, masks[0]);
  }
  
  // Se houver múltiplas máscaras, aplica todas e escolhe a melhor
  const results = masks.map(m => ({
    mask: m,
    result: applySingleMask(value, m),
    consumed: applySingleMask(value, m).replace(/[^\w]/g, '').length
  }));
  
  // Escolhe a máscara que consumiu mais caracteres do valor
  const best = results.reduce((prev, curr) => 
    curr.consumed > prev.consumed ? curr : prev
  );
  
  return best.result;
};

function evaluateDisabledRules(rules, inputs, lockupFields) {
  //return false;
  if (!rules) return false;
  // Exemplo de regra: {campo}=0;{outro}="abc";{idade}=18
  return rules.split(';').some(rule => {
    const [left, right] = rule.split('=').map(s => s.trim());
    // Resolve o lado esquerdo (variável ou valor fixo)
    let leftValue = left;
    if (/^\{(.+)\}$/.test(left)) {
      const key = left.match(/^\{(.+)\}$/)[1];
      leftValue = lockupFields[key] ?? inputs[key];
    } else if (/^".*"$/.test(left)) {
      leftValue = left.slice(1, -1);
    } else if (!isNaN(left)) {
      leftValue = Number(left);
    }
    // Resolve o lado direito (variável, string ou número)
    let rightValue = right;
    if (/^\{(.+)\}$/.test(right)) {
      const key = right.match(/^\{(.+)\}$/)[1];
      rightValue = lockupFields[key] ?? inputs[key];
    } else if (/^".*"$/.test(right)) {
      rightValue = right.slice(1, -1);
    } else if (!isNaN(right)) {
      rightValue = Number(right);
    }
    return leftValue === rightValue;
  });
}

function Component({ data, inputs, handleChange, handleBlur, screenAction, handleShowModal, getShowModal, currentJson, lockupFields, inputRef, handleFocus, formatLabel, handleRecalculate, touchedComputedFields, autocompleteKeys, resolver, objectType, filterOperators, setFilterOperators }) {
  const navigate = useNavigate();
  // Suporta tanto enabledOn (whitelist) quanto disabledOn (blacklist)
  const disabledByScreenAction = screenAction && (
    (data.enabledOn && !data.enabledOn.includes(screenAction)) ||
    (data.disabledOn && data.disabledOn.includes(screenAction))
  );
  
  // 🔒 NOVO: Modo detail desabilita TODOS os campos automaticamente (exceto botões de ação)
  const disabledByDetailMode = screenAction === 'detail' && !['submitbutton', 'showbutton', 'label', 'separator'].includes(data.type?.toLowerCase());
  
  // Combina todas as condições de disabled
  const isDisabled = data.disabledRule 
    ? evaluateDisabledRules(data.disabledRule, inputs, lockupFields) 
    : disabledByScreenAction || disabledByDetailMode || data.disabled || data.disabledByConnection;

  // Helper para renderizar campo com refresh icon quando computedFrom existe
  const renderWithRefreshIcon = (inputElement) => {
    if (!data.computedFrom) {
      return inputElement;
    }
    
    const isTouched = touchedComputedFields?.has(data.nameForm);
    
    return (
      <InputGroup>
        {inputElement}
        <InputGroup.Text 
          style={{ 
            cursor: 'pointer',
            backgroundColor: isTouched ? '#f8f9fa' : '#e9ecef',
            borderColor: '#ced4da'
          }}
          onClick={() => handleRecalculate?.(data.nameForm)}
          title="Recalcular valor"
        >
          🔄
        </InputGroup.Text>
      </InputGroup>
    );
  };

  // Resolve valor exibido para o componente, suportando nameForm vazio e computedName
  const resolveValue = (d) => {
    if (!d) return '';
    // Se tem nameForm, prioriza inputs[nameForm]
    if (d.nameForm) {
      if (d.field) return (lockupFields?.[d.field]) ?? (inputs?.[d.nameForm]) ?? '';
      // Prioriza lockupFields quando existir (computedFrom grava em lockupFields)
      return (lockupFields?.[d.nameForm]) ?? (inputs?.[d.nameForm]) ?? '';
    }

    // Se não tem nameForm, usa computedName se informado
    if (d.computedName) {
      return (lockupFields?.[d.computedName]) ?? (inputs?.[d.computedName]) ?? '';
    }

    // Se tem field (lookup), retorna lockupFields[field]
    if (d.field) return (lockupFields?.[d.field]) ?? '';

    return '';
  };


  switch (data.type.toLowerCase()) {
    case 'submitbutton':
      return  <div className={data.class} style={data.style}>
                <Button
                  variant="primary" type="submit"
                  size="sm" style={{"width": "100%", "height": "100%"}}
                  disabled={isDisabled}
                >
                  {data.name}
                </Button>
              </div> 
    case 'text':
      {
        const displayValue = resolveValue(data);
        // Se for um objeto do tipo 'filter', exibe seletor de operador ao lado
        const showOperator = objectType === 'filter' && setFilterOperators;
        const currentOp = filterOperators?.[data.nameForm] || 'contains';

        // Mantém a classe externa configurada (data.class) e cria uma linha interna
        // onde a coluna interna ocupa full-width (12) para preservar o input dominante
        const classString = data.class || '';
        // Procura por col-{break}-{size} ou col-{size}
        const colMatch = classString.match(/col-([a-z]+)-(\d{1,2})/) || classString.match(/col-(\d{1,2})/);
        // Por padrão, quando não há classe col-* definida no componente,
        // mantém a coluna interna como full-width (12) para preencher a coluna externa
        let leftSize = 12, rightSize = 0, leftClass = 'col-12', rightClass = 'col-0';
        if (colMatch) {
          if (colMatch.length === 3) {
            const br = colMatch[1];
            const sz = parseInt(colMatch[2], 10);
            // Mantém a coluna interna ocupando toda a largura (12) sob o mesmo breakpoint
            leftClass = `col-${br}-12`;
            rightClass = `col-${br}-0`;
          } else if (colMatch.length === 2) {
            const sz = parseInt(colMatch[1], 10);
            leftClass = `col-12`;
            rightClass = `col-0`;
          }
        }

        const opLabel = currentOp === 'contains' ? '%' : currentOp === 'eq' ? '=' : currentOp === 'starts' ? '^' : '$';

        return (
          <div className={data.class} style={data.style}>
            <div className="row g-0">
              <div className={leftClass}>
                <Form.Group controlId={data.nameForm} style={{paddingRight: 8}}>
                  <Form.Label>{data.name}</Form.Label>
                  {renderWithRefreshIcon(
                    <InputGroup className="flex-nowrap">
                      <Form.Control
                        required={data.required}
                        type="text"
                        placeholder={data.placeholder}
                        name={data.nameForm}
                        value={displayValue}
                        onChange={data.mask ?
                          (e) => {
                            const rawValue = e.target.value.replace(/[^\\w]/g, ''); // Remove caracteres não alfanuméricos
                            const maskedValue = applyMask(rawValue, data.mask);
                            handleChange({...e, target: {name: data.nameForm, value: maskedValue}});
                          } :
                          handleChange
                        }
                        data-events={JSON.stringify(data.events)}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        ref={inputRef}
                        maxLength={data.mask ? (Array.isArray(data.mask) ? Math.max(...data.mask.map(m => m.length)) : data.mask.length) : data.maxLength}
                        disabled={isDisabled}
                      />
                      {showOperator && (
                        <InputGroup.Text style={{padding: 0, border: 'none'}}>
                          <Dropdown drop="start" as={ButtonGroup}>
                            <Dropdown.Toggle variant="light" id={`op-${data.nameForm}`} size="sm" disabled={isDisabled} style={{padding: '0.2rem 0.45rem', border: '1px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <FilterListIcon fontSize="small" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => setFilterOperators(prev => ({ ...prev, [data.nameForm]: 'contains' }))} active={currentOp === 'contains'}>Contenha</Dropdown.Item>
                              <Dropdown.Item onClick={() => setFilterOperators(prev => ({ ...prev, [data.nameForm]: 'eq' }))} active={currentOp === 'eq'}>Igual</Dropdown.Item>
                              <Dropdown.Item onClick={() => setFilterOperators(prev => ({ ...prev, [data.nameForm]: 'starts' }))} active={currentOp === 'starts'}>Inicie com</Dropdown.Item>
                              <Dropdown.Item onClick={() => setFilterOperators(prev => ({ ...prev, [data.nameForm]: 'ends' }))} active={currentOp === 'ends'}>Termina em</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </InputGroup.Text>
                      )}
                    </InputGroup>
                  )}
                </Form.Group>
              </div>
            </div>
          </div>
        )
      }
    case 'textarea':
      {
        const displayValue = resolveValue(data);
        return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <Form.Control 
                  required={data.required}
                  as="textarea"
                  rows={data.rows || 2}
                  placeholder={data.placeholder}
                  name={data.nameForm}
                  value={displayValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  maxLength={data.maxLength}
                  ref={inputRef}
                  disabled={isDisabled}
                />
              </Form.Group>
      }
    case 'number':
      {
        const displayValue = resolveValue(data);
        return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                {renderWithRefreshIcon(
                  <Form.Control 
                    required={data.required}
                    type="number"
                    placeholder={data.placeholder}
                    name={data.nameForm}
                    value={displayValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    ref={inputRef}
                    maxLength={data.maxLength}
                    disabled={isDisabled}
                  />
                )}
              </Form.Group>
      }
    case 'autocomplete':
      return <Autocomplete
                key={autocompleteKeys?.[data.nameForm] || 0}
                data={data}
                inputs={inputs}
                lockupFields={lockupFields}
                resolver={resolver}
                value={inputs[data.params?.key] || null}
                handleChange={handleChange}
                formatLabel={formatLabel}
                disabled={isDisabled}
                objectType={objectType}
                filterOperators={filterOperators}
                setFilterOperators={setFilterOperators}
              />
    case 'email':
      {
        const displayValue = resolveValue(data);
        return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <Form.Control 
                  required={data.required}
                  type="email"
                  placeholder={data.placeholder}
                  name={data.nameForm}
                  value={displayValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  maxLength={data.maxLength}
                  disabled={isDisabled}
                  isInvalid={displayValue && !validateEmail(displayValue)}
                />
                <Form.Control.Feedback type="invalid">
                  Insira um email válido.
                </Form.Control.Feedback>
              </Form.Group>
      }
    case 'cpf':
      {
        const displayValue = resolveValue(data);
        return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <Form.Control 
                  required={data.required}
                  type="text"
                  placeholder={data.placeholder}
                  name={data.nameForm}
                  value={displayValue}
                  onChange={e => handleChange({...e, target: {name: data.nameForm, value: formatCPF(e.target.value)}})}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  maxLength={14}
                  isInvalid={displayValue && !validateCPF(displayValue)}
                  disabled={isDisabled}
                />
                <Form.Control.Feedback type="invalid">
                  Insira um CPF válido.
                </Form.Control.Feedback>
              </Form.Group>
      }
    case 'cpfcnpj':
      {
        const displayValue = resolveValue(data);
        return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <Form.Control 
                  required={data.required}
                  type="text"
                  placeholder={data.placeholder}
                  name={data.nameForm}
                  value={displayValue}
                  onChange={e => handleChange({...e, target: {name: data.nameForm, value: formatCPFCNPJ(e.target.value)}})}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  maxLength={18}
                  isInvalid={displayValue && !validateCPFCNPJ(displayValue)}
                  disabled={isDisabled}
                />
                <Form.Control.Feedback type="invalid">
                  Insira um CPF ou CNPJ válido.
                </Form.Control.Feedback>
              </Form.Group>
      }
    case 'phonenumber':
      {
        const displayValue = resolveValue(data);
        return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <Form.Control 
                  required={data.required}
                  type="text"
                  placeholder={data.placeholder}
                  name={data.nameForm}
                  value={displayValue}
                  onChange={e => handleChange({...e, target: {name: data.nameForm, value: formatPhoneNumber(e.target.value)}})}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  maxLength={13}
                  isInvalid={displayValue && !validatePhoneNumber(displayValue)}
                  disabled={isDisabled}
                />
                <Form.Control.Feedback type="invalid">
                  Insira um número válido.
                </Form.Control.Feedback>
              </Form.Group>
      }
    case 'currency':
        let currencyValue = '';
        let currencyNumericValue = null;
        if (data.expression) {
          // Substitui {campo} pelos valores de inputs ou lookupFields
          let formula = data.expression.replace(/\{(\w+)\}/g, (match, key) => {
            // Verifica primeiro inputs, depois lockupFields (dados do banco)
            const rawValue = inputs[key] || lockupFields[key] || '';
            
            // Converte para string
            const strValue = rawValue.toString();
            
            // Detecta o formato: se tem vírgula, é formato BR (1.234,56)
            // se só tem ponto, é formato numérico (1.5 ou 1234.56)
            let valor;
            if (strValue.includes(',')) {
              // Formato brasileiro: remove pontos (milhar) e troca vírgula por ponto (decimal)
              valor = strValue.replace(/\./g, '').replace(',', '.');
            } else {
              // Formato numérico direto: mantém como está
              valor = strValue;
            }
            
            return Number(valor) || 0;
          });
          try {
              let result = eval(formula); // Cuidado: só use eval se confiar na origem da expressão!
              currencyNumericValue = result;
              
              if (data.variant === 'currency') {
                const decimal = typeof data.decimal === 'number' ? data.decimal : 2;
                currencyValue = formatCurrency(result, decimal, data.symbol);
              } else if (data.variant === 'decimal') {
                const decimal = typeof data.decimal === 'number' ? data.decimal : 2;
                currencyValue = formatDecimal(result, decimal);
              } else {
                currencyValue = result;
              }
              
              // Armazena o valor formatado com separador de milhar para enviar à API
              if (currencyNumericValue !== null) {
                const decimal = typeof data.decimal === 'number' ? data.decimal : 2;
                const rounded = Number(currencyNumericValue.toFixed(decimal));
                inputs[data.nameForm] = formatDecimal(rounded, decimal);
              } else {
                inputs[data.nameForm] = '0';
              }
            } catch (e) {
              currencyValue = '';
              inputs[data.nameForm] = '0';
            }
        }
      return <Form.Group className={data.class} controlId={data.nameForm}>
              <Form.Label>{data.name}</Form.Label>
              <InputGroup >
                <InputGroup.Text id="basic-addon1" style={{"marginBottom": "5px"}}>{data.symbol || "R$"}</InputGroup.Text>
                <NumericFormat
                  required={data.required}
                  thousandSeparator={'.'}
                  decimalSeparator={','}
                  decimalScale={data.decimal || 2}
                  fixedDecimalScale={true}
                  placeholder={data.placeholder}
                  name={data.nameForm}
                  value={(() => {
                    let val = resolveValue(data);
                    // Se valor está vazio/undefined/null, usa "0,00" para mostrar zero formatado
                    if (val === '' || val === null || val === undefined) {
                      return '0';
                    }
                    return val;
                  })()}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  disabled={isDisabled}
                  customInput={Form.Control}
                  className="form-control"
                />
                {data.computedFrom && (
                  <InputGroup.Text 
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: touchedComputedFields?.has(data.nameForm) ? '#f8f9fa' : '#e9ecef',
                      borderColor: '#ced4da',
                      marginBottom: "5px"
                    }}
                    onClick={() => handleRecalculate?.(data.nameForm)}
                    title="Recalcular valor"
                  >
                    🔄
                  </InputGroup.Text>
                )}
              </InputGroup>
      </Form.Group>
    case 'decimal':
        let decimalValue = '';
        let decimalNumericValue = null;
        if (data.expression) {
          // Substitui {campo} pelos valores de inputs ou lookupFields
          let formula = data.expression.replace(/\{(\w+)\}/g, (match, key) => {
            // Verifica primeiro inputs, depois lockupFields (dados do banco)
            const rawValue = inputs[key] || lockupFields[key] || '';
            
            // Converte para string
            const strValue = rawValue.toString();
            
            // Detecta o formato: se tem vírgula, é formato BR (1.234,56)
            // se só tem ponto, é formato numérico (1.5 ou 1234.56)
            let valor;
            if (strValue.includes(',')) {
              // Formato brasileiro: remove pontos (milhar) e troca vírgula por ponto (decimal)
              valor = strValue.replace(/\./g, '').replace(',', '.');
            } else {
              // Formato numérico direto: mantém como está
              valor = strValue;
            }
            
            return Number(valor) || 0;
          });
          try {
              let result = eval(formula); // Cuidado: só use eval se confiar na origem da expressão!
              decimalNumericValue = result;
              const decimal = typeof data.decimal === 'number' ? data.decimal : 2;
              decimalValue = formatDecimal(result, decimal);
              
              // Armazena o valor formatado com separador de milhar para enviar à API
              if (decimalNumericValue !== null) {
                const rounded = Number(decimalNumericValue.toFixed(decimal));
                inputs[data.nameForm] = formatDecimal(rounded, decimal);
              } else {
                inputs[data.nameForm] = '0';
              }
            } catch (e) {
              decimalValue = '';
              inputs[data.nameForm] = '0';
            }
        }
      return <Form.Group className={data.class} controlId={data.nameForm}>
              <Form.Label>{data.name}</Form.Label>
              {data.computedFrom ? (
                <InputGroup>
                  <NumericFormat
                    required={data.required}
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    decimalScale={data.decimal || 2}
                    fixedDecimalScale={true}
                    placeholder={data.placeholder}
                    name={data.nameForm}
                    value={(() => {
                      let val = resolveValue(data);
                      // Se valor está vazio/undefined/null, usa "0" para mostrar zero formatado
                      if (val === '' || val === null || val === undefined) {
                        return '0';
                      }
                      return val;
                    })()}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    ref={inputRef}
                    disabled={isDisabled}
                    customInput={Form.Control}
                    className="form-control"
                  />
                  <InputGroup.Text 
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: touchedComputedFields?.has(data.nameForm) ? '#f8f9fa' : '#e9ecef',
                      borderColor: '#ced4da'
                    }}
                    onClick={() => handleRecalculate?.(data.nameForm)}
                    title="Recalcular valor"
                  >
                    🔄
                  </InputGroup.Text>
                </InputGroup>
              ) : (
                <NumericFormat
                  required={data.required}
                  thousandSeparator={'.'}
                  decimalSeparator={','}
                  decimalScale={data.decimal || 2}
                  fixedDecimalScale={true}
                  placeholder={data.placeholder}
                  name={data.nameForm}
                  value={(() => {
                    let val = resolveValue(data);
                    // Se valor está vazio/undefined/null, usa "0" para mostrar zero formatado
                    if (val === '' || val === null || val === undefined) {
                      return '0';
                    }
                    return val;
                  })()}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  disabled={isDisabled}
                  customInput={Form.Control}
                  className="form-control"
                />
              )}
      </Form.Group>
    case 'instagram':
      {
        const displayValue = resolveValue(data);
        return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <InputGroup >
                  <InputGroup.Text id="basic-addon1" style={{"marginBottom": "5px"}}>@</InputGroup.Text>
                  <Form.Control 
                    required={data.required}
                    type="text"
                    placeholder={data.placeholder}
                    name={data.nameForm}
                    value={displayValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    ref={inputRef}
                    maxLength={data.maxLength}
                    disabled={isDisabled}
                  />
                </InputGroup>
              </Form.Group>
      }
    case 'password':
      const handleMouseDown = () => {
        if (inputRef.current) {
          inputRef.current.type = 'text';
        }
      };

      const handleMouseUp = () => {
        if (inputRef.current) {
          inputRef.current.type = 'password';
        }
      };

      return <Form.Group className={data.class} controlId={data.nameForm} type="password">
								<Form.Label>{data.name}</Form.Label>
                <InputGroup>
                  <Form.Control
                    ref={inputRef}
                    required={data.required}
                    type="password"
                    placeholder={data.placeholder}
                    name={data.nameForm}
                    value={inputs[data.nameForm] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    maxLength={data.maxLength}
                    disabled={isDisabled}
                    isInvalid={data.invalid}
                  />
                  <Button
                    variant="outline-secondary"
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp} // garante que a senha volte se sair do botão
                    style={{"marginBottom": "5px", "borderRadius": "0 0.375rem 0.375rem 0"}}
                  >
                    <VisibilityIcon fontSize="small"/>
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {data.invalidMessage}
                  </Form.Control.Feedback>
                </InputGroup>
							</Form.Group>
    case 'select':
      return  <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <Form.Select
                  name={data.nameForm}
                  value={resolveValue(data)}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  disabled={isDisabled}
                >
                  {data.options?.map((option, index) => (
                    <option key={index} value={option.value}>{option.text}</option>
                  ))}
                </Form.Select>
              </Form.Group>
    case 'date':
      return  <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <DatePicker
                  selected={(!data.range && resolveValue(data)) || null}
                  onChange={newDate => data.range ? 
                    handleChange({target: {name: data.nameForm, value: { startDate: newDate[0], endDate: newDate[1] }}}) :
                    handleChange({target: {name: data.nameForm, value: newDate}})}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  dateFormat={data.variant === "year" ? "yyyy" 
                    : data.variant === "month" ? "MM/yyyy"
                    : data.variant === "datetime" ? "Pp"
                    : data.variant === "time" ? "HH:mm" : "dd/MM/yyyy"}
                  placeholderText={data.placeholder}
                  className="form-control"
                  locale={pt}
                  showMonthYearPicker={data.variant === "month"}
                  showFullMonthYearPicker
                  showYearPicker={data.variant === "year"}
                  minDate={addDays(parseDate(data.minDate), 1)}
                  maxDate={(inputs[data.nameForm]?.startDate && data.maxDays) ? addDays(inputs[data.nameForm].startDate, data.maxDays-1) : addDays(parseDate(data.maxDate), 1)}
                  startDate={(data.range && inputs[data.nameForm]?.startDate) || null}
                  endDate={(data.range && inputs[data.nameForm]?.endDate) || null}
                  selectsRange={data.range}
                  isClearable={data.clearable}
                  withPortal={data.modal}
                  disabled={isDisabled}
                  showTimeSelect={data.variant?.includes("time")}
                  showTimeSelectOnly={data.variant === "time"}
                  timeFormat="p"
                  timeIntervals={data.timeIntervals} // Em minutos
                  showTimeCaption={false}
                />

              </Form.Group>
    case 'radiogroup':
      return  <RadioGroup
                data={data}
                inputs={inputs}
                handleChange={handleChange}
                disabled={isDisabled}
              />
    case 'groupcheckbox':
      return  <GroupCheckbox
                data={data}
                inputs={inputs}
                handleChange={handleChange}
                disabled={isDisabled}
              />
    case 'checkbox':
      return  <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Check
                  required={data.required}
                  type={data.variant || "checkbox"}
                  label={data.name}
                  checked={inputs[data.nameForm] === data.checkedValue || data.uncheckedValue}
                  onChange={e => handleChange({...e, target: {name: data.nameForm, value: e.target.checked ? data.checkedValue : data.uncheckedValue}})}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={inputRef}
                  disabled={isDisabled}
                />
              </Form.Group>
    case 'range':
      return  <Range
                data={data}
                inputs={inputs}
                handleChange={handleChange}
                disabled={isDisabled}
              />
    case 'fileupload':
      return  <FileUpload
                data={data}
                fileData={inputs[data.nameForm] || null}
                download={data.download && screenAction && screenAction !== 'create'}
                handleChange={handleChange}
                disabled={isDisabled}
              />
    case 'cep':
      return <CEP
                data={data}
                inputs={inputs}
                handleChange={handleChange}
                disabled={isDisabled}
              />
    case 'showbutton':
      return  <div className={data.class} style={data.style}>
                <Button
                  variant={data.variant || "primary"} type="buton"
                  size="sm" style={{"width": "100%", "height": "100%"}}
                  disabled={isDisabled}
                  onClick={() => handleShowModal(!getShowModal(), data.showObject)}
                >
                  {data.name}
                </Button>
              </div>
    case 'linkpanel': {
      // Resolve template variables {{campo}} contra inputs
      const resolveTpl = (tpl) => (typeof tpl === 'string'
        ? tpl.replace(/{{\s*([\w-]+)\s*}}/g, (_, k) => (inputs?.[k] ?? lockupFields?.[k] ?? ''))
        : tpl);
      const titleTxt = resolveTpl(data.title || data.name || '');
      const descTxt = resolveTpl(data.description || '');
      const icon = data.icon || 'bi bi-box-arrow-up-right';
      const iconColor = data.iconColor || '#0d6efd';
      const iconBg = data.iconBg || '#e7f1ff';
      // Monta URL com params
      const buildHref = () => {
        const url = resolveTpl(data.url || '');
        if (!url) return null;
        const isExternal = /^https?:\/\//i.test(url);
        let path = isExternal ? url : (url.startsWith('/') ? url : `/home/${url}`);
        if (data.params && typeof data.params === 'object' && !Array.isArray(data.params)) {
          const qs = Object.entries(data.params)
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(resolveTpl(String(v)))}`)
            .join('&');
          if (qs) path += (path.includes('?') ? '&' : '?') + qs;
        }
        return { path, isExternal };
      };
      const href = buildHref();
      const handleClick = (e) => {
        if (!href || isDisabled) { e.preventDefault(); return; }
        if (href.isExternal) return; // deixa o browser navegar
        e.preventDefault();
        navigate(href.path);
      };
      return (
        <div className={data.class || 'col-md-12'} style={{ marginBottom: 10, ...(data.style || {}) }}>
          <a
            href={href?.path || '#'}
            onClick={handleClick}
            target={data.target || (href?.isExternal ? '_blank' : undefined)}
            rel={href?.isExternal ? 'noopener noreferrer' : undefined}
            className="link-panel"
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '16px 20px', border: '1px solid #dee2e6',
              borderRadius: 12, textDecoration: 'none', color: '#212529',
              background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.04)',
              transition: 'all .2s', cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (isDisabled) return;
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.10)';
              e.currentTarget.style.borderColor = iconColor;
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.04)';
              e.currentTarget.style.borderColor = '#dee2e6';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={{
              flex: '0 0 56px', height: 56, borderRadius: 12,
              background: iconBg, color: iconColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.6rem',
            }}>
              <i className={icon}></i>
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{titleTxt}</div>
              {descTxt && (
                <div style={{ color: '#6c757d', fontSize: '.875rem' }}>{descTxt}</div>
              )}
            </span>
            <i className="bi bi-chevron-right" style={{ color: '#adb5bd', fontSize: '1.2rem' }}></i>
          </a>
        </div>
      );
    }
    case 'label':
      function formatString(template, data) {
        // Replace variables in the template
        const result = template?.replace(/{{\s*([\w-]+)\s*}}/g, (match, key) => {
            if (key in data) {
                return data[key];
            } else {
                return '';
            }
        });
    
        return result;
      } 

      let value = inputs[data.nameForm] || formatString(data.nameForm, inputs) || data.nameForm || ''

      return <Form.Group className={data.class} controlId={data.nameForm}>
                {data.name && <Form.Label>{formatText(data.name, data.case)}</Form.Label>}
                {data.nameForm &&
                  <Form.Control
                    plaintext
                    as="div"
                    readOnly
                  >
                    {value.split("\n").map((line, index) => (
                        <React.Fragment key={index}>
                            {formatText(line, data.case)}
                            <br />
                        </React.Fragment>
                    ))}
                  </Form.Control>
                }
              </Form.Group>
    case 'total':
      let totalValue = 0
      let totals = []
      let expression = data.expression || '';

      const getData = (entity, field, filters) => {
        // Defensive: some entries in currentJson.entities may be undefined
        const found = currentJson?.entities?.find(e => e && e.entity === entity);
        let data = (found && found.data) || [];

        // Helper to resolve variable references ({{var}}) searching in currentJson, inputs and lockupFields
        const resolveVariable = (variableName) => {
          if (currentJson?.objects) {
            for (let obj of currentJson.objects) {
              if (obj.data && obj.data[variableName] !== undefined) {
                return obj.data[variableName];
              }
            }
          }

          if (currentJson?.entities) {
            for (let entityObj of currentJson.entities) {
              if (entityObj.data) {
                if (Array.isArray(entityObj.data)) {
                  for (let item of entityObj.data) {
                    if (item[variableName] !== undefined) {
                      return item[variableName];
                    }
                  }
                } else if (entityObj.data[variableName] !== undefined) {
                  return entityObj.data[variableName];
                }
              }
            }
          }

          if (currentJson?.connections) {
            for (let connection of currentJson.connections) {
              if (connection.data && connection.data[variableName] !== undefined) {
                return connection.data[variableName];
              }
            }
          }

          if (currentJson?.data && currentJson.data[variableName] !== undefined) {
            return currentJson.data[variableName];
          }

          if (inputs && inputs[variableName] !== undefined) {
            return inputs[variableName];
          }

          if (lockupFields && lockupFields[variableName] !== undefined) {
            return lockupFields[variableName];
          }

          return null;
        };

        if (filters) {
          filters.forEach(filter => {
            let filterVal = filter.value;
            if (typeof filterVal === 'string' && filterVal.startsWith('{{') && filterVal.endsWith('}}')) {
              const referenced = filterVal.replace('{{', '').replace('}}', '');
              filterVal = resolveVariable(referenced);
            }

            data = data.filter(item => {
              // Use loose equality to allow type coercion similar to previous behavior
              return item[filter.field] == filterVal;
            });
          });
        }

        return data.map(item => parseFloat(item[field]) || 0);
      }

      if (expression && currentJson && currentJson.entities) {
        let fields = expression.fields || [];
        for (let field of fields) {
          // Defensive: ignore falsy/malformed field entries
          if (!field || typeof field !== 'object') continue;
          let type = field.type || 'sum';
          let fieldName = field.name || '';
          let entity = field.entity || '';
          let fieldTotal = 0

          if (fieldName && entity) {
            if (type === 'sum') {
              fieldTotal += getData(entity, fieldName, field.filters)?.reduce((acc, val) => new Decimal(acc).plus(val), 0) || 0;
            } else if (type === 'average') {
              let values = getData(entity, fieldName, field.filters);
              fieldTotal = values.length > 0 ? values.reduce((acc, val) => new Decimal(acc).plus(val), 0) / values.length : 0;
            } else if (type === 'max') {
              fieldTotal = Math.max(...(getData(entity, fieldName, field.filters) || []) || []);
              if (fieldTotal === -Infinity) {
                fieldTotal = 0; // Se não houver valores, define como 0
              }
            } else if (type === 'min') {
              fieldTotal = Math.min(...(getData(entity, fieldName, field.filters) || []) || []);
              if (fieldTotal === Infinity) {
                fieldTotal = 0; // Se não houver valores, define como 0
              }
            }
          }

          if (field.negative) {
            fieldTotal = -fieldTotal; // Inverte o sinal se necessário
          }

          totals.push(fieldTotal)
        }

        if (expression.type === 'sum') {
          totalValue = totals.reduce((acc, val) => new Decimal(acc).plus(val), 0);
          // totalValue = Math.round(totalValue * 100) / 100;
        } else if (expression.type === 'average') {
          totalValue = totals.length > 0 ? totals.reduce((acc, val) => new Decimal(acc).plus(val), 0) / totals.length : 0;
        } else if (expression.type === 'max') {
          totalValue = Math.max(...totals);
        } else if (expression.type === 'min') {
          totalValue = Math.min(...totals);
        }
      }

      return <Form.Group className={data.class} controlId={data.nameForm}>
                {data.name && <Form.Label>{formatText(data.name, data.case)}</Form.Label>}
                <Form.Control
                  plaintext
                  as="div"
                  readOnly
                >
                  {data.variant === 'currency' ? formatCurrency(totalValue, data.decimal, data.symbol) : totalValue}
                </Form.Control>
              </Form.Group>
    default:
      return <Form.Group className={data.class} controlId={data.nameForm}>
                <Form.Label>{data.name}</Form.Label>
                <div>Componente inválido</div>
              </Form.Group>
  }
}

export default Component;
